import { useEffect, useState } from 'react';

/**
 * 浮层位置：「打开那一刻算一次，之后永不变」。
 *
 * 为什么这条是硬要求而不是审美：地面站里浮层上全是按钮，位置一跳，
 * 手指/鼠标按下去的点与最终命中的按钮就不是同一个了 —— 那就是误发指令。
 * 内容一变化就重新居中（很多弹窗库的默认行为）正是最常见的位置跳动来源。
 *
 * 因此这里的策略是三条彼此独立、都能单独验证的规则：
 *
 * 1. **不在渲染期读面板自身尺寸**：位置只由「锚点矩形 + 视口尺寸」决定，
 *    而这两样都在打开时快照一次就冻结。没有测量 ⇒ 没有"内容变高 ⇒ 重新测量 ⇒ 位置跳"这条路。
 * 2. **一律向下展开，靠 `maxHeight` + 内部滚动吸收多余高度**：
 *    不做「下面放不下就翻到上面」的翻转 —— 翻转需要知道内容高度，又把测量引回来了。
 * 3. **居中是与内容无关的常量**：无锚点时用 `left: 50%` + `translateX(-50%)`，
 *    它由视口决定，内容宽度变化不会让它移动（垂直方向则有冻结的 `top`）。
 *
 * 代价（明确写下）：视口尺寸在浮层打开期间变化（窗口拉伸）时浮层不会跟随。
 * 地面站基本都是全屏作业，这个代价远小于"操作中浮层自己挪位"的风险；
 * 需要新位置就关掉重开。
 */

export interface AnchorRect {
  readonly top: number;
  readonly bottom: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

export interface ViewportRect {
  readonly width: number;
  readonly height: number;
}

export interface FrozenPlacement {
  /** 距视口顶部的像素值，冻结于打开那一刻 */
  readonly top: number;
  /** 有锚点时的左边距像素值；水平居中时无意义（见 `centered`） */
  readonly left: number;
  /** 是否水平居中。居中由视口与 transform 决定，因此内容变化不会移动它 */
  readonly centered: boolean;
  readonly maxWidth: number;
  /** 形如 `calc(100vh - 128px - 8px)`：把"还剩多少空间"交给 CSS，避免在 JS 里测量面板高度 */
  readonly maxHeight: string;
}

/** 视口边缘留白 */
export const OVERLAY_MARGIN = 8;
/** 锚点与浮层之间的间隙 */
export const OVERLAY_ANCHOR_GAP = 8;
/** 浮层至少要在视口里露出的高度：露一条也比整块跑到视口外（等于"点不到"）强 */
export const OVERLAY_MIN_VISIBLE_HEIGHT = 96;
export const OVERLAY_MIN_PANEL_WIDTH = 176;
export const OVERLAY_DEFAULT_MAX_WIDTH = 480;
/**
 * 无锚点时的默认位置：视口顶部往下约 1/6。
 * 刻意不放在正中 —— 地面站中央通常是姿态/地图这类必须一直可见的实时区域，
 * 而浮层默认就是 realtime 作用域（不挡遥测）。
 */
export const OVERLAY_TOP_RATIO = 0.16;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * 纯函数：给定锚点与视口，算出唯一的位置。不碰 DOM，因此可以直接单测。
 */
export function computeFrozenPlacement(
  anchor: AnchorRect | null,
  viewport: ViewportRect,
  maxWidth: number = OVERLAY_DEFAULT_MAX_WIDTH,
): FrozenPlacement {
  const width = Math.round(viewport.width);
  const height = Math.round(viewport.height);
  const widthLimit = Math.max(OVERLAY_MIN_PANEL_WIDTH, Math.min(maxWidth, width - 2 * OVERLAY_MARGIN));
  const topLimit = Math.max(OVERLAY_MARGIN, height - OVERLAY_MIN_VISIBLE_HEIGHT);
  const top = clamp(
    Math.round(anchor === null ? height * OVERLAY_TOP_RATIO : anchor.bottom + OVERLAY_ANCHOR_GAP),
    OVERLAY_MARGIN,
    topLimit,
  );
  const maxHeight = `calc(100vh - ${top}px - ${OVERLAY_MARGIN}px)`;

  if (anchor === null) {
    return { top, left: 0, centered: true, maxWidth: widthLimit, maxHeight };
  }

  // 有锚点就贴着锚点左边缘，但不许从右边溢出视口
  const left = clamp(Math.round(anchor.left), OVERLAY_MARGIN, Math.max(OVERLAY_MARGIN, width - widthLimit - OVERLAY_MARGIN));
  return { top, left, centered: false, maxWidth: widthLimit, maxHeight };
}

const CLOSED_PLACEMENT: FrozenPlacement = {
  top: OVERLAY_MARGIN,
  left: 0,
  centered: true,
  maxWidth: OVERLAY_DEFAULT_MAX_WIDTH,
  maxHeight: `calc(100vh - ${OVERLAY_MARGIN}px - ${OVERLAY_MARGIN}px)`,
};

/**
 * 冻结的几何量。
 *
 * 锚点矩形与位置**必须来自同一次快照**：飞入动画的起点用「锚点矩形 − 冻结位置」算，
 * 如果两者分别测量，中间任何一次布局变化（滚动、内容变高）都会让起点指向一个
 * 与最终位置无关的方向 —— 表现为“从别的地方飞过来”。
 */
export interface FrozenGeometry {
  readonly placement: FrozenPlacement;
  readonly anchor: AnchorRect | null;
}

const CLOSED_GEOMETRY: FrozenGeometry = { placement: CLOSED_PLACEMENT, anchor: null };

  function snapshot(anchor: Element | null, maxWidth: number | undefined): FrozenGeometry {
  if (typeof window === 'undefined') return CLOSED_GEOMETRY;
  const rect = anchor === null ? null : anchor.getBoundingClientRect();
  const anchorRect: AnchorRect | null =
    rect === null ? null : { top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width, height: rect.height };
  return {
    anchor: anchorRect,
    placement: computeFrozenPlacement(
      anchorRect,
      { width: window.innerWidth, height: window.innerHeight },
      maxWidth,
    ),
  };
}

/**
 * 冻结的位置与锚点矩形。
 *
 * 用 `useState` + 渲染期赋值（React 官方的「渲染期间调整状态」写法）而不是 `useRef`：
 * 状态更新会立刻重渲染，所以**第一帧提交出去的就是最终位置**。
 * 换成 `useEffect`（或 `useLayoutEffect`）会在提交之后再挪一次，那一次跳变正是要消灭的东西；
 * 而渲染期写 ref 在 React 的纯函数假设下是不安全的（渲染可能被丢弃）。
 */
export function useFrozenGeometry(options: {
  open: boolean;
  /**
   * 锚点。类型是 `Element` 而不是 `HTMLElement`：这里只用它取几何（`getBoundingClientRect`），
   * 而触发件可能是 SVG 图标的包裹层；把类型收紧只会逼调用点去断言。
   */
  anchor: Element | null;
  maxWidth: number | undefined;
}): FrozenGeometry {
  const [frozen, setFrozen] = useState<FrozenGeometry | null>(null);

  if (options.open && frozen === null) {
    setFrozen(snapshot(options.anchor, options.maxWidth));
  }

  useEffect(() => {
    // 关闭后清空：下次打开要按当时的锚点重新冻结，而不是复用上一次的位置。
    // （放在 effect 里而不是渲染期，避免"关闭时顺手改了状态"引发额外往返。）
    if (!options.open && frozen !== null) setFrozen(null);
  }, [options.open, frozen]);

  return frozen ?? CLOSED_GEOMETRY;
}

/** 只关心位置时用这个（`useFrozenGeometry` 的薄包装） */
export function useFrozenPlacement(options: {
  open: boolean;
  anchor: Element | null;
  maxWidth: number | undefined;
}): FrozenPlacement {
  return useFrozenGeometry(options).placement;
}
